import React from 'react';
import classnames from 'classnames';
import {
  ThemeOptions,
  ThemeOverride,
  ThemeSkin,
} from '@wix/da-react-context/pkg/ThemeContext';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import PositionOnlyPopperPopover from '@wix/da-ds/pkg/popper/presets/PositionOnlyPopperPopover';
import LoadingIndicator from '@wix/da-shared-react/pkg/LoadingIndicator';
import { useTranslation } from 'react-i18next';

import s from './ConfigPopover.scss';

export interface Props {
  height?: number;
  withSaveButton?: boolean;
  saveButtonDisabled?: boolean;
  isLoading?: boolean;
  className?: string;
  onClose(): void;
  onSave?(): void;
  usePortal?: boolean;
  shouldCloseOnClickOutside?: boolean;
  children?: React.ReactNode;
}

const POPOVER_WIDTH = 266;
const POPOVER_HEIGHT_WHEN_LOADING = 166;

const ConfigPopover: React.FC<Props> = ({
  height,
  usePortal = false,
  withSaveButton = false,
  saveButtonDisabled,
  isLoading,
  shouldCloseOnClickOutside = true,
  className,
  onClose,
  onSave,
  children,
}) => {
  const complete_theme = useFlag('complete_theme');
  const { t } = useTranslation();

  return (
    <PositionOnlyPopperPopover
      wrapperClassName={classnames('no-theme-skin', s['root'], className)}
      popoverContent={
        <ThemeOverride
          tag="div"
          className={s['theme-override-wrapper']}
          theme={complete_theme ? 'match' : ThemeOptions.LIGHT}
          themeSkin={complete_theme ? ThemeSkin.TERTIARY : undefined}
        >
          {isLoading ? (
            <LoadingIndicator coverMode />
          ) : (
            <>
              {children}
              {withSaveButton && (
                <div className={s['footer']}>
                  <BrandedButton
                    fillContainerWidth
                    size="large"
                    variant="brand-green"
                    disabled={saveButtonDisabled}
                    onClick={onSave}
                  >
                    {t('common.save')}
                  </BrandedButton>
                </div>
              )}
            </>
          )}
        </ThemeOverride>
      }
      width={POPOVER_WIDTH}
      height={isLoading ? POPOVER_HEIGHT_WHEN_LOADING : height}
      onClickOutside={shouldCloseOnClickOutside ? onClose : undefined}
      className={s['ref-style']}
      contentClassName={s['tooltip-content']}
      placement="bottom"
      secondaryPlacement="end"
      usePortal={usePortal}
    />
  );
};
ConfigPopover.displayName = 'ConfigPopover';

export default ConfigPopover;
